import React, { useEffect, useContext } from 'react';
import { useParams } from 'react-router';
import { Form, Col, Row } from 'react-bootstrap';
import AppContext from 'Utils/AppContext';
import ProdToggle from './ProdToggle';
import {
  SAAS_SALESFORCE_TYPE,
  SALESFORCE_LOGIN_URL_PRODUCTION,
  SALESFORCE_LOGIN_URL_SANDBOX,
  SALESFORCE_DEFAULT_API_VERSION,
} from 'DataSources/datasourcesApi';

/**
 * Editor for a Salesforce org (engine 'salesforce', datasourceType SAAS). There is no host /
 * port / database: the org is addressed by its login URL and authenticated with one of two
 * OAuth flows the connected app supports —
 *
 *   JWT_BEARER          consumer key + integration username + RS256 private key (PEM)
 *   CLIENT_CREDENTIALS  consumer key + client secret
 *
 * The username-password flow is retired by Salesforce and deliberately not offered. Secrets
 * (privateKeyPem / clientSecret) follow the same rule as every other editor: blank on edit means
 * "keep the stored value", and the platform only ever echoes them redacted.
 */
export const LOGIN_URL_MODE = {
  PRODUCTION: 'PRODUCTION',
  SANDBOX: 'SANDBOX',
  CUSTOM: 'CUSTOM',
};

export function loginUrlModeOf(loginUrl) {
  const u = String(loginUrl || '').trim().replace(/\/+$/, '').toLowerCase();
  if (!u || u === SALESFORCE_LOGIN_URL_PRODUCTION) return LOGIN_URL_MODE.PRODUCTION;
  if (u === SALESFORCE_LOGIN_URL_SANDBOX) return LOGIN_URL_MODE.SANDBOX;
  return LOGIN_URL_MODE.CUSTOM;
}

/**
 * Per-connection overrides of the worker's `tdm.salesforce.*` knobs, sent as `config.advanced`.
 * Blank means "use the platform default", so nothing here is pre-filled. Key names and ranges are
 * kept in lock-step with SalesforceAdvanced (c2-tdm-service) and validateSaasAdvanced (platform).
 */
export const SALESFORCE_ADVANCED_FIELDS = [
  { key: 'apiReserve', label: 'API call reserve', help: 'Refuse to spend below this many of the org’s remaining daily API calls. A Developer Edition org has 15,000 a day; an Enterprise org has millions.' },
  { key: 'apiReservePercent', label: 'API reserve (%)', help: 'The same floor as a percentage of the daily allowance. 0-100.' },
  { key: 'inChunkIds', label: 'Ids per SOQL IN chunk', help: 'Also bounded by Salesforce’s 100k-character statement limit.' },
  { key: 'wholeObjectThresholdKeys', label: 'Whole-object export threshold', help: 'Above this many keys a hop switches from IN chunks to a Bulk 2.0 whole-object export plus client-side filter.' },
  { key: 'queryBatchSize', label: 'Query batch size', help: 'Rows per REST query page.' },
  { key: 'ingestChunkBytes', label: 'Bulk ingest chunk (bytes)', help: 'Salesforce caps a Bulk 2.0 upload at 100 MB; the default leaves headroom.' },
  { key: 'bisectMinRows', label: 'Bisect floor (rows)', help: 'A failing batch is bisected down to this size before rows go to failed/.' },
  { key: 'bulkPollMaxSeconds', label: 'Bulk poll interval (s)', help: 'Upper bound on the backoff between Bulk job status polls.' },
  { key: 'bulkJobTimeoutMinutes', label: 'Bulk job timeout (min)', help: 'How long to wait for one Bulk job before failing the run.' },
  { key: 'httpTimeoutSeconds', label: 'HTTP timeout (s)', help: 'Per-request timeout against this org.' },
  { key: 'targetUsernameSuffix', label: 'Provisioned username suffix', help: 'Appended when provisioning users into a target org — Salesforce usernames are globally unique. Letters, digits or ._%+- only.' },
];

const SalesforceSourceEditor = ({ connection, setConnection }) => {
  const { rulesList } = useContext(AppContext);
  const { id } = useParams();

  const defaultRules = (rulesList || []).find((rules) => rules.default === true);

  const defaults = {
    name: '',
    loginUrl: SALESFORCE_LOGIN_URL_PRODUCTION,
    authFlow: 'JWT_BEARER',
    consumerKey: '',
    username: '',
    privateKeyPem: '',
    clientSecret: '',
    apiVersion: SALESFORCE_DEFAULT_API_VERSION,
    type: SAAS_SALESFORCE_TYPE,
    ...!id && ({
      scheduleEnabled: true,
      scheduleDayOfWeek: 'SAT',
      scheduleWeekOccurrence: 1,
      scheduleHour: 0,
      scheduleMinute: 0,
      scheduleRules: defaultRules,
    }),
  };

  // Merge defaults UNDER whatever the parent dropdown / a ?prefill= payload already set —
  // same pattern as the Mongo and Dynamo editors.
  useEffect(() => { if (!id) setConnection((prev) => ({ ...defaults, ...(prev || {}) })); }, []);

  const authFlow = String(connection.authFlow || 'JWT_BEARER').toUpperCase();
  const isJwt = authFlow !== 'CLIENT_CREDENTIALS';
  const urlMode = loginUrlModeOf(connection.loginUrl);

  // The radios are a shortcut for typing a login URL. They no longer set any org-family flag:
  // the JWT audience defaults to whatever this URL is.
  const pickLoginUrlMode = (mode) => {
    if (mode === LOGIN_URL_MODE.PRODUCTION) {
      setConnection({ ...connection, loginUrl: SALESFORCE_LOGIN_URL_PRODUCTION });
    } else if (mode === LOGIN_URL_MODE.SANDBOX) {
      setConnection({ ...connection, loginUrl: SALESFORCE_LOGIN_URL_SANDBOX });
    } else {
      // Keep whatever is typed unless it is one of the two well-known hosts, which would
      // immediately snap the radio back; a My Domain URL is the usual thing to enter here.
      const current = String(connection.loginUrl || '');
      const keep = loginUrlModeOf(current) === LOGIN_URL_MODE.CUSTOM ? current : 'https://';
      setConnection({ ...connection, loginUrl: keep });
    }
  };


  // Omit rather than blank: an absent key means "use the platform default", and writing defaults
  // into the saved config would freeze today's values into this source forever.
  const setAdvanced = (key, raw) => {
    const next = { ...(connection.advanced || {}) };
    const v = String(raw ?? '').trim();
    if (v === '') delete next[key]; else next[key] = v;
    const out = { ...connection };
    if (Object.keys(next).length) out.advanced = next; else delete out.advanced;
    setConnection(out);
  };

  return (
    <div className='text-left'>
      <Row className='mb-4'>
        <Col lg='12'>
          <Form.Label>Data source name *</Form.Label>
          <Form.Control
            size='sm'
            placeholder='Salesforce_Prod'
            value={connection.name || ''}
            onChange={(n) => setConnection({ ...connection, name: n.target.value })}
            type='text'
            required
            isInvalid={!!(connection && connection.name && connection.name.includes(' '))}
          />
          <Form.Control.Feedback type='invalid'>
            Data source names must not include spaces.
          </Form.Control.Feedback>
        </Col>
      </Row>
      <ProdToggle />
      <Row className='mb-3'>
        <Col>
          <Form.Label>Login URL *</Form.Label>
          <div data-testid='salesforce-login-url-mode'>
            <Form.Check
              inline
              type='radio'
              id='sf-login-production'
              name='sf-login-mode'
              label='Production'
              checked={urlMode === LOGIN_URL_MODE.PRODUCTION}
              onChange={() => pickLoginUrlMode(LOGIN_URL_MODE.PRODUCTION)}
            />
            <Form.Check
              inline
              type='radio'
              id='sf-login-sandbox'
              name='sf-login-mode'
              label='Sandbox / scratch org'
              checked={urlMode === LOGIN_URL_MODE.SANDBOX}
              onChange={() => pickLoginUrlMode(LOGIN_URL_MODE.SANDBOX)}
            />
            <Form.Check
              inline
              type='radio'
              id='sf-login-custom'
              name='sf-login-mode'
              label='My Domain'
              checked={urlMode === LOGIN_URL_MODE.CUSTOM}
              onChange={() => pickLoginUrlMode(LOGIN_URL_MODE.CUSTOM)}
            />
          </div>
          <Form.Control
            size='sm'
            type='url'
            data-testid='salesforce-login-url'
            value={connection.loginUrl ?? ''}
            readOnly={urlMode !== LOGIN_URL_MODE.CUSTOM}
            placeholder='https://acme.my.salesforce.com'
            onChange={(n) => setConnection({ ...connection, loginUrl: n.target.value })}
            required
          />
          <Form.Text className='text-muted'>
            {isJwt
              ? 'Your org’s My Domain URL, or one of the shared login hosts. This is also the JWT audience unless you override it under Advanced.'
              : 'Client credentials must use your org’s own My Domain URL — Salesforce does not serve that flow from login.salesforce.com or test.salesforce.com.'}
          </Form.Text>
        </Col>
      </Row>
      <Row className='mb-3'>
        <Col>
          <Form.Label>Authentication *</Form.Label>
          <Form.Control
            as='select'
            size='sm'
            data-testid='salesforce-auth-flow'
            value={isJwt ? 'JWT_BEARER' : 'CLIENT_CREDENTIALS'}
            onChange={(e) => setConnection({ ...connection, authFlow: e.target.value })}
          >
            <option value='JWT_BEARER'>JWT bearer (certificate)</option>
            <option value='CLIENT_CREDENTIALS'>Client credentials (secret)</option>
          </Form.Control>
          <Form.Text className='text-muted'>
            Both flows need a connected app with the API scope. JWT bearer also needs the app’s
            certificate and &ldquo;admin-approved users are pre-authorized&rdquo;.
          </Form.Text>
        </Col>
      </Row>
      <Row className='mb-3'>
        <Col className='pr-1'>
          <Form.Label>Consumer key *</Form.Label>
          <Form.Control
            size='sm'
            type='text'
            data-testid='salesforce-consumer-key'
            value={connection.consumerKey ?? ''}
            onChange={(n) => setConnection({ ...connection, consumerKey: n.target.value })}
            placeholder='3MVG9…'
            autoComplete='off'
            required
          />
          <Form.Text className='text-muted'>The connected app’s consumer key (client id).</Form.Text>
        </Col>
        {isJwt && (
          <Col className='pl-1'>
            <Form.Label>Integration username *</Form.Label>
            <Form.Control
              size='sm'
              type='text'
              data-testid='salesforce-username'
              value={connection.username ?? ''}
              onChange={(n) => setConnection({ ...connection, username: n.target.value })}
              placeholder='integration@acme.com'
              autoComplete='off'
              required
            />
            <Form.Text className='text-muted'>The user the JWT is issued for.</Form.Text>
          </Col>
        )}
      </Row>
      {isJwt ? (
        <Row className='mb-3'>
          <Col>
            <Form.Label>Private key (PEM) {id ? '' : '*'}</Form.Label>
            <Form.Control
              as='textarea'
              rows={5}
              size='sm'
              data-testid='salesforce-private-key'
              style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
              value={connection.privateKeyPem ?? ''}
              placeholder={id
                ? 'Leave blank to keep the stored key'
                : '-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----'}
              onChange={(n) => setConnection({ ...connection, privateKeyPem: n.target.value })}
              autoComplete='off'
              required={!id}
            />
            <Form.Text className='text-muted'>
              The private key matching the certificate uploaded to the connected app. Stored encrypted;
              never shown again after saving.
            </Form.Text>
          </Col>
        </Row>
      ) : (
        <Row className='mb-3'>
          <Col>
            <Form.Label>Client secret {id ? '' : '*'}</Form.Label>
            <Form.Control
              size='sm'
              type='password'
              data-testid='salesforce-client-secret'
              value={connection.clientSecret ?? ''}
              placeholder={id ? 'Leave blank to keep the stored secret' : 'Consumer secret'}
              onChange={(n) => setConnection({ ...connection, clientSecret: n.target.value })}
              autoComplete={id ? 'off' : 'new-password'}
              required={!id}
            />
            <Form.Text className='text-muted'>
              The connected app’s consumer secret. The app must have &ldquo;Enable Client Credentials
              Flow&rdquo; on, with a run-as user assigned.
            </Form.Text>
          </Col>
        </Row>
      )}
      <Form.Check
        type='switch'
        id='sf-advanced-toggle'
        data-testid='salesforce-advanced-toggle'
        className='mb-2 mt-2'
        label='Advanced connection options'
        checked={connection._showAdvanced === true}
        onChange={(e) => setConnection({ ...connection, _showAdvanced: e.target.checked })}
      />
      {connection._showAdvanced && (
        <Row className='mb-3'>
          <Col lg='12' className='mb-3'>
            <Form.Label>JWT audience</Form.Label>
            <Form.Control
              size='sm'
              type='url'
              data-testid='salesforce-audience'
              value={connection.audience ?? ''}
              placeholder={connection.loginUrl || 'https://acme.my.salesforce.com'}
              onChange={(n) => setConnection({ ...connection, audience: n.target.value })}
            />
            <Form.Text className='text-muted'>
              Leave blank to use the login URL, which is what almost every org wants. Set it only if
              Salesforce rejects the audience — it returns
              &ldquo;invalid_grant: audience is invalid&rdquo; and names nothing else.
            </Form.Text>
          </Col>
          <Col lg='6' className='mb-3'>
            <Form.Label>API version</Form.Label>
            <Form.Control
              size='sm'
              type='text'
              data-testid='salesforce-api-version'
              value={connection.apiVersion ?? ''}
              placeholder={SALESFORCE_DEFAULT_API_VERSION}
              onChange={(n) => setConnection({ ...connection, apiVersion: n.target.value })}
            />
            <Form.Text className='text-muted'>Defaults to {SALESFORCE_DEFAULT_API_VERSION}.</Form.Text>
          </Col>
          {SALESFORCE_ADVANCED_FIELDS.map((f) => (
            <Col lg='6' className='mb-3' key={f.key}>
              <Form.Label>{f.label}</Form.Label>
              <Form.Control
                size='sm'
                type='text'
                data-testid={`salesforce-advanced-${f.key}`}
                value={(connection.advanced || {})[f.key] ?? ''}
                placeholder='Platform default'
                onChange={(n) => setAdvanced(f.key, n.target.value)}
              />
              <Form.Text className='text-muted'>{f.help}</Form.Text>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default SalesforceSourceEditor;
